import { useEffect, useMemo, useState } from "react";
import { api, API, fmtMoney, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, FileSpreadsheet, Calendar, Play } from "lucide-react";
import LedgerDrillDown from "@/components/common/LedgerDrillDown";

const AS_OF_PRESETS = [
    { v: "today", l: "Today" },
    { v: "this_month_end", l: "End of This Month" },
    { v: "previous_month_end", l: "End of Previous Month" },
    { v: "this_quarter_end", l: "End of This Quarter" },
    { v: "previous_quarter_end", l: "End of Previous Quarter" },
    { v: "fy_end", l: "End of This FY (Mar 31)" },
    { v: "previous_fy_end", l: "End of Previous FY" },
    { v: "custom", l: "Custom Date" },
];

function asOfFromPreset(preset, custom) {
    const t = new Date(today() + "T00:00:00");
    const fmt = (d) => d.toISOString().slice(0, 10);
    switch (preset) {
        case "today": return fmt(t);
        case "this_month_end": return fmt(new Date(t.getFullYear(), t.getMonth() + 1, 0));
        case "previous_month_end": return fmt(new Date(t.getFullYear(), t.getMonth(), 0));
        case "this_quarter_end": {
            const q = Math.floor(t.getMonth() / 3);
            return fmt(new Date(t.getFullYear(), q * 3 + 3, 0));
        }
        case "previous_quarter_end": {
            const q = Math.floor(t.getMonth() / 3) - 1;
            const year = q < 0 ? t.getFullYear() - 1 : t.getFullYear();
            const qq = (q + 4) % 4;
            return fmt(new Date(year, qq * 3 + 3, 0));
        }
        case "fy_end": {
            const y = t.getMonth() < 3 ? t.getFullYear() : t.getFullYear() + 1;
            return fmt(new Date(y, 2, 31));
        }
        case "previous_fy_end": {
            const y = t.getMonth() < 3 ? t.getFullYear() - 1 : t.getFullYear();
            return fmt(new Date(y, 2, 31));
        }
        case "custom": return custom || today();
        default: return today();
    }
}

export default function BalanceSheet() {
    const [preset, setPreset] = useState("today");
    const [customAsOf, setCustomAsOf] = useState(today());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [drill, setDrill] = useState({ open: false, accountId: null, accountName: "" });

    const asOf = useMemo(() => asOfFromPreset(preset, customAsOf), [preset, customAsOf]);
    const isCustom = preset === "custom";

    const openDrill = (row) => {
        if (!row.id || row.id.startsWith("retained_")) return;
        setDrill({ open: true, accountId: row.id, accountName: row.name });
    };

    const run = async () => {
        setLoading(true);
        try { const { data } = await api.get("/reports/balance-sheet", { params: { as_of: asOf } }); setData(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const exportFile = async (format) => {
        const win = format === "pdf" ? window.open("", "_blank") : null;
        const token = localStorage.getItem("vyastra_token");
        const res = await fetch(`${API}/reports/balance-sheet/export?as_of=${asOf}&format=${format}`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        if (format === "pdf") {
            if (win && !win.closed) win.location.href = blobUrl;
            else { const a = document.createElement("a"); a.href = blobUrl; a.target = "_blank"; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove(); }
        } else {
            const ext = format === "xlsx" ? "xlsx" : "csv";
            const a = document.createElement("a");
            a.href = blobUrl; a.download = `BalanceSheet_${asOf}.${ext}`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(blobUrl);
        }
    };

    return (
        <>
            <PageHeader kicker="REPORTS" title="Balance Sheet" subtitle="Assets, liabilities, and equity at a point in time. Click any account to drill into its ledger." />
            <PageBody>
                <div className="bg-card border border-border rounded-md p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">As of</Label>
                        <Select value={preset} onValueChange={setPreset}>
                            <SelectTrigger className="h-9 w-[210px]" data-testid="bs-preset">
                                <Calendar className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {AS_OF_PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {isCustom && (
                        <div className="flex flex-col gap-1.5">
                            <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Date</Label>
                            <Input type="date" value={customAsOf} onChange={(e) => setCustomAsOf(e.target.value)} className="h-9 w-[160px]" data-testid="bs-asof" />
                        </div>
                    )}
                    {!isCustom && (
                        <div className="flex flex-col gap-1.5 text-[11.5px] text-muted-foreground self-end pb-1.5">
                            {asOf}
                        </div>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="bs-export-pdf"><FileText className="w-3.5 h-3.5 mr-1" />PDF</Button>
                        <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} data-testid="bs-export-xlsx"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel</Button>
                        <Button variant="outline" size="sm" onClick={() => exportFile("csv")} data-testid="bs-export-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
                        <Button size="sm" onClick={run} data-testid="bs-run"><Play className="w-3.5 h-3.5 mr-1.5" />Run Report</Button>
                    </div>
                </div>
                {loading && <div className="text-center text-muted-foreground py-12">Loading…</div>}
                {data && !loading && (
                    <div className="bg-card border border-border rounded-md p-6 md:p-8 max-w-[1000px] mx-auto" data-testid="bs-report">
                        <div className="text-center pb-6 border-b border-border">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-gold">Balance Sheet</div>
                            <div className="font-serif text-2xl mt-1">As of {data.as_of}</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                            <div>
                                <h3 className="text-[10px] uppercase tracking-[0.16em] text-gold mb-3">Assets</h3>
                                {data.assets.map((a) => <Row key={a.id} row={a} onClick={openDrill} />)}
                                <div className="border-t border-border mt-3 pt-2"><Row row={{ name: "Total Assets", amount: data.totals.assets }} bold large /></div>
                            </div>
                            <div>
                                <h3 className="text-[10px] uppercase tracking-[0.16em] text-gold mb-3">Liabilities & Equity</h3>
                                {data.liabilities.map((a) => <Row key={a.id} row={a} onClick={openDrill} />)}
                                <div className="border-t border-border mt-2 pt-2"><Row row={{ name: "Total Liabilities", amount: data.totals.liabilities }} bold /></div>
                                <div className="mt-3">
                                    {data.equity.map((a) => <Row key={a.id} row={a} onClick={openDrill} />)}
                                    <div className="border-t border-border mt-2 pt-2"><Row row={{ name: "Total Equity", amount: data.totals.equity }} bold /></div>
                                </div>
                                <div className="border-t-2 border-foreground mt-3 pt-2"><Row row={{ name: "Liabilities + Equity", amount: data.totals.liab_plus_equity }} bold large /></div>
                            </div>
                        </div>
                    </div>
                )}
            </PageBody>
            <LedgerDrillDown
                open={drill.open}
                onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}
                accountId={drill.accountId}
                accountName={drill.accountName}
                dateFrom={"1900-01-01"}
                dateTo={asOf}
            />
        </>
    );
}

function Row({ row, bold, large, onClick }) {
    const clickable = onClick && row.id && !String(row.id).startsWith("retained_");
    return (
        <div
            className={`flex justify-between py-1 ${bold ? "font-medium" : ""} ${large ? "text-[16px]" : "text-[13px]"} ${clickable ? "cursor-pointer hover:text-gold hover:underline underline-offset-2 decoration-gold/40" : ""}`}
            onClick={clickable ? () => onClick(row) : undefined}
            data-testid={row.id ? `bs-row-${row.id}` : undefined}
            title={clickable ? "Click to view ledger transactions" : undefined}
        >
            <span>{row.name}</span>
            <span className="font-mono">{fmtMoney(row.amount)}</span>
        </div>
    );
}
